/**
 * Lightweight JWT payload decoder. Signature validation is the resource
 * server's job (wsr-api) — this is a read-only client-side parse used only to
 * surface the operator's identity (email / name / tenant / role) in the UI.
 *
 * Returns `null` on any malformed input rather than throwing, so callers can
 * branch cleanly without try/catch noise.
 *
 * Claim names are taken from the AUTHORITATIVE consumer, wsr-api's
 * `SecurityConfig` + `TenantScope` (HANDOFF-wsr-api-platform-foundation §4.2),
 * which validate these exact tokens:
 *   - tenant slug: `tenant_slug` (fallbacks `tenantSlug`, `tenant`)
 *   - roles:       `roles` (a string[] of `ROLE_*`)
 *   - tenant role: `tenant_role` (a bare role string, e.g. `OWNER` / `ADMIN`;
 *                  wsr-api prefixes it with `ROLE_`)
 * Identity (`sub`, `email`, `name`, `aud`, `exp`) are the standard saas-auth
 * claims. The tenant slug comes ONLY from the JWT (never a path/body on an
 * authenticated endpoint) — the platform tenancy rule wsr-api enforces.
 */
export interface JwtPayload {
  sub?: string;
  aud?: string | string[];
  exp?: number;
  iat?: number;
  email?: string;
  name?: string;
  // Tenant claim (+ accepted fallbacks), mirroring wsr-api TenantScope.
  tenant_slug?: string;
  tenantSlug?: string;
  tenant?: string;
  // Role claims, mirroring wsr-api's JWT-granted-authorities conversion.
  roles?: string[];
  tenant_role?: string;
  // Platform principal discriminator (saas-auth SASATH-0313). Only present on
  // non-member/non-staff tokens; test for the exact VALUE, never for presence.
  principal_type?: string;
  [key: string]: unknown;
}

export function decodeJwtPayload(token: string | null | undefined): JwtPayload | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const payloadB64 = parts[1];
  if (!payloadB64) return null;
  try {
    const base64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json =
      typeof atob === 'function' ? atob(padded) : Buffer.from(padded, 'base64').toString('utf-8');
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

/** Tenant slug from the claims, honoring the wsr-api fallback order. */
export function tenantSlugFromClaims(claims: JwtPayload | null): string | undefined {
  if (!claims) return undefined;
  for (const value of [claims.tenant_slug, claims.tenantSlug, claims.tenant]) {
    if (typeof value === 'string' && value.trim().length > 0) return value;
  }
  return undefined;
}

/**
 * Normalized role set (without the `ROLE_` prefix). Combines the `roles` array
 * (stripping any `ROLE_` prefix) with the singular `tenant_role` claim, matching
 * how wsr-api builds its `ROLE_*` authorities for `hasAnyRole('OWNER','ADMIN')`.
 */
export function rolesFromClaims(claims: JwtPayload | null): string[] {
  if (!claims) return [];
  const out = new Set<string>();
  if (Array.isArray(claims.roles)) {
    for (const r of claims.roles) {
      if (typeof r === 'string' && r.trim()) out.add(r.replace(/^ROLE_/, ''));
    }
  }
  if (typeof claims.tenant_role === 'string' && claims.tenant_role.trim()) {
    out.add(claims.tenant_role.replace(/^ROLE_/, ''));
  }
  return [...out];
}
